import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import { inputClass, labelClass, thClass, tdClass } from './adminUi';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { name: '', slug: '', parent_slug: '', description: '', image: '' };

const AdminCategories = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () =>
    axios
      .get(`${API}/admin/categories`)
      .then((r) => setItems(Array.isArray(r.data) ? r.data : r.data.categories || []))
      .catch(() => toast.error('Échec du chargement des catégories'))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const startCreate = () => {
    setForm(EMPTY);
    setEditingId(null);
    setOpen(true);
  };
  const startEdit = (c) => {
    setForm({ name: c.name || '', slug: c.slug || '', parent_slug: c.parent_slug || '', description: c.description || '', image: c.image || '' });
    setEditingId(c.id);
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) await axios.put(`${API}/admin/categories/${editingId}`, form);
      else await axios.post(`${API}/admin/categories`, form);
      toast.success(editingId ? 'Catégorie mise à jour' : 'Catégorie créée');
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Supprimer la catégorie « ${c.name} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/categories/${c.id}`);
      toast.success('Catégorie supprimée');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div data-testid="admin-categories">
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-2xl">Catégories</h2>
        <button onClick={startCreate} className="btn-primary inline-flex items-center gap-2" data-testid="cat-new">
          <Plus size={16} /> Nouvelle catégorie
        </button>
      </div>

      {loading ? (
        <p className="text-foreground/40">Chargement…</p>
      ) : items.length === 0 ? (
        <p className="text-foreground/40 py-8">Aucune catégorie. Crée la première.</p>
      ) : (
        <div className="overflow-x-auto border border-black/10">
          <table className="w-full">
            <thead>
              <tr>
                <th className={thClass}>Nom</th>
                <th className={thClass}>Slug</th>
                <th className={thClass}>Parent</th>
                <th className={thClass}></th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id}>
                  <td className={tdClass}>{c.name}</td>
                  <td className={tdClass}>{c.slug}</td>
                  <td className={tdClass}>{c.parent_slug || '—'}</td>
                  <td className={`${tdClass} text-right whitespace-nowrap`}>
                    <button onClick={() => startEdit(c)} className="text-foreground/50 hover:text-gold mr-3"><Pencil size={16} /></button>
                    <button onClick={() => remove(c)} className="text-foreground/50 hover:text-red-400"><Trash2 size={16} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-background border border-black/10 p-6 w-full max-w-lg space-y-4">
            <div className="flex justify-between items-center">
              <h3 className="font-heading text-xl">{editingId ? 'Modifier' : 'Nouvelle'} catégorie</h3>
              <button type="button" onClick={() => setOpen(false)}><X size={20} className="text-foreground/50" /></button>
            </div>
            <div>
              <label className={labelClass}>Nom *</label>
              <input className={inputClass} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="cat-name" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelClass}>Slug (auto si vide)</label>
                <input className={inputClass} value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Catégorie parente (slug)</label>
                <input className={inputClass} value={form.parent_slug} onChange={(e) => setForm({ ...form, parent_slug: e.target.value })} placeholder="ex: vetements" />
              </div>
            </div>
            <div>
              <label className={labelClass}>Description</label>
              <textarea className={`${inputClass} min-h-[80px]`} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Image (URL)</label>
              <input className={inputClass} value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} />
            </div>
            <button type="submit" className="btn-primary w-full" data-testid="cat-submit">{editingId ? 'Enregistrer' : 'Créer'}</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
